import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
// Le Toaster passe par le wrapper local (et non `sonner` directement) : c'est
// lui qui répercute le thème actif sur les toasts.
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
// La page passager reste dans le bundle initial : c'est le chemin critique
// (scan QR sur mobile). Tout l'admin est chargé à la demande.
import ScanPage from "@/pages/ScanPage";

const LoginPage = lazy(() => import("@/pages/LoginPage"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const DashboardPage = lazy(() => import("@/pages/admin/DashboardPage"));
const CampaignsPage = lazy(() => import("@/pages/admin/CampaignsPage"));
const DrawsPage = lazy(() => import("@/pages/admin/DrawsPage"));
const DrawStatsPage = lazy(() => import("@/pages/admin/DrawStatsPage"));
const PrizesPage = lazy(() => import("@/pages/admin/PrizesPage"));
const SponsorsPage = lazy(() => import("@/pages/admin/SponsorsPage"));
const CompaniesPage = lazy(() => import("@/pages/admin/CompaniesPage"));
const WalletsPage = lazy(() => import("@/pages/admin/WalletsPage"));
const WalletDetailPage = lazy(() => import("@/pages/admin/WalletDetailPage"));
const DriversPage = lazy(() => import("@/pages/admin/DriversPage"));
const DriverPerformancePage = lazy(() => import("@/pages/admin/DriverPerformancePage"));
const RemunerationPage = lazy(() => import("@/pages/admin/RemunerationPage"));
const FinancePage = lazy(() => import("@/pages/admin/FinancePage"));
// Page publique : un chauffeur consulte ses gains avec son seul numéro de
// téléphone — aucun compte, le lien est partagé depuis le module Chauffeurs.
const DriverEarningsPage = lazy(() => import("@/pages/DriverEarningsPage"));
const VehiclesPage = lazy(() => import("@/pages/admin/VehiclesPage"));
const GeographyPage = lazy(() => import("@/pages/admin/GeographyPage"));
const QrCodesPage = lazy(() => import("@/pages/admin/QrCodesPage"));
const WinnersPage = lazy(() => import("@/pages/admin/WinnersPage"));
const AnalyticsPage = lazy(() => import("@/pages/admin/AnalyticsPage"));
const AiChatPage = lazy(() => import("@/pages/admin/AiChatPage"));
const BrandingPage = lazy(() => import("@/pages/admin/BrandingPage"));
const UsersPage = lazy(() => import("@/pages/admin/UsersPage"));
const ActivityPage = lazy(() => import("@/pages/admin/ActivityPage"));

// Portail partenaire — espace annonceur, séparé de la console ops jusqu'à la
// page de connexion : /portal/login n'authentifie que les comptes entreprise.
const PortalLoginPage = lazy(() => import("@/pages/portal/PortalLoginPage"));
const PortalLayout = lazy(() => import("@/pages/portal/PortalLayout"));
const PortalDashboardPage = lazy(() => import("@/pages/portal/PortalDashboardPage"));
const PortalCampaignsPage = lazy(() => import("@/pages/portal/PortalCampaignsPage"));
const PortalCampaignStatsPage = lazy(() => import("@/pages/portal/PortalCampaignStatsPage"));
const PortalDrawsPage = lazy(() => import("@/pages/portal/PortalDrawsPage"));
const PortalDrawStatsPage = lazy(() => import("@/pages/portal/PortalDrawStatsPage"));
const PortalUsersPage = lazy(() => import("@/pages/portal/PortalUsersPage"));
const PortalWalletPage = lazy(() => import("@/pages/portal/PortalWalletPage"));

const PageLoader = (
  <div className="min-h-screen grid place-items-center">
    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
  </div>
);

/* Seules ces routes suivent le thème clair / sombre / système. Tout le reste —
   la page passager /scan, la racine et le fourre-tout qui y redirigent — reste
   en clair : c'est une surface de MARQUE, dessinée sur fond blanc (ticket à
   gratter, créations des annonceurs, logos sponsors fournis en PNG clair), et
   le passager n'a rien demandé ni rien à régler.

   Liste positive plutôt que « tout sauf /scan » : la racine et les URL
   inconnues redirigent vers /scan, et les exclure une à une laissait une image
   sombre le temps de la redirection.

   ATTENTION — la même liste est dupliquée dans public/index.html, où un script
   tranche le thème AVANT la première peinture. Les deux doivent rester
   alignées, sinon la page passager clignote en sombre au chargement. */
const THEMED_ROUTES = /^\/(admin|portal|login|chauffeur)(\/|$)/;

/* Le thème se décide sous le routeur, puisqu'il dépend de la route : d'où un
   composant intermédiaire, seul endroit où `useLocation` est disponible. */
function ThemedRoutes() {
  const { pathname } = useLocation();

  return (
    <ThemeProvider forcedTheme={THEMED_ROUTES.test(pathname) ? undefined : "light"}>
      <Suspense fallback={PageLoader}>
        <Routes>
          <Route path="/" element={<Navigate to="/scan" replace />} />
          <Route path="/scan" element={<ScanPage />} />
          <Route path="/chauffeur" element={<DriverEarningsPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="companies" element={<CompaniesPage />} />
            <Route path="wallets" element={<WalletsPage />} />
            <Route path="wallets/:companyId" element={<WalletDetailPage />} />
            <Route path="campaigns" element={<CampaignsPage />} />
            <Route path="draws" element={<DrawsPage />} />
            <Route path="draws/:id/stats" element={<DrawStatsPage />} />
            <Route path="prizes" element={<PrizesPage />} />
            <Route path="winners" element={<WinnersPage />} />
            <Route path="sponsors" element={<SponsorsPage />} />
            <Route path="drivers" element={<DriversPage />} />
            <Route path="drivers/performance" element={<DriverPerformancePage />} />
            <Route path="remuneration" element={<RemunerationPage />} />
            <Route path="finance" element={<FinancePage />} />
            <Route path="vehicles" element={<VehiclesPage />} />
            <Route path="geography" element={<GeographyPage />} />
            <Route path="qrcodes" element={<QrCodesPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="assistant" element={<AiChatPage />} />
            <Route path="branding" element={<BrandingPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="activity" element={<ActivityPage />} />
          </Route>
          {/* Déclarée avant la coquille /portal : c'est la seule route du
              portail accessible sans session. */}
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalDashboardPage />} />
            <Route path="campaigns" element={<PortalCampaignsPage />} />
            <Route path="campaigns/:id" element={<PortalCampaignStatsPage />} />
            <Route path="draws" element={<PortalDrawsPage />} />
            <Route path="draws/:id" element={<PortalDrawStatsPage />} />
            <Route path="wallet" element={<PortalWalletPage />} />
            <Route path="users" element={<PortalUsersPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/scan" replace />} />
        </Routes>
      </Suspense>
      {/* Sous le fournisseur de thème, donc accordé à la route affichée : un
          toast sombre serait sinon le seul élément sombre de la page. */}
      <Toaster position="top-right" richColors />
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemedRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
